// Maps an authenticated request path to a Champion Activity section bucket.
// Used by session middleware to write rows to user_page_visits, and indirectly
// by the engagement dashboard to summarize where Champions spend their time.
//
// The taxonomy here is intentionally coarse — Barb thinks in sections
// ("community feed", "lesson page"), not raw routes. Add new sections only
// when there's a real product question to answer at that grain. New RPC
// prefixes that don't fit an existing section land in `other` so we notice
// them in logs without losing the row.

export type Section =
  | 'course_home'
  | 'lesson_page'
  | 'community_feed'
  | 'community_meetings'
  | 'community_people'
  | 'community_library'
  | 'discussion_thread'
  | 'meeting_page'
  | 'meetings_list'
  | 'my_courses'
  | 'admin_dashboard'
  | 'other';

export type ClassificationBody = {
  courseId?: string;
  discussionId?: string;
  lessonId?: string;
  meetingId?: string;
};

export type Classification = {
  section: Section;
  courseId?: string;
  discussionId?: string;
  lessonId?: string;
  meetingId?: string;
};

// Path prefixes that should never be tracked. Health checks, polling, static
// assets, dynamic CSS, file downloads, embed shells — all noise.
const NOISE_PREFIXES = [
  '/api/status/server',
  '/rpx/notifications/getUnreadNotificationCount',
  '/rpx/chats/getRoomUpdates',
  '/bundle-',
  '/dist/',
  '/service-worker',
  '/manifest.json',
  '/icons/',
  '/favicon',
  '/api/tenant.css',
  '/api/course_css/',
  '/files/',
  '/embed/iframe',
];

// Ordered list — first matching entry wins. Each entry maps an exact RPC
// prefix to a section and which body fields to capture as side-channels.
type Mapping = {
  prefix: string;
  section: Section;
  capture?: ('courseId' | 'discussionId' | 'lessonId' | 'meetingId')[];
};

const RPC_MAPPINGS: Mapping[] = [
  { prefix: '/rpx/courses/getStudentCourse', section: 'course_home', capture: ['courseId'] },
  { prefix: '/rpx/courses/getMyCourses', section: 'my_courses' },
  {
    prefix: '/rpx/lessons/getFullLessonState',
    section: 'lesson_page',
    capture: ['courseId', 'lessonId'],
  },
  { prefix: '/rpx/lessons/getLesson', section: 'lesson_page', capture: ['courseId', 'lessonId'] },
  { prefix: '/rpx/miraseeCommunity/getCommunityFeed', section: 'community_feed' },
  { prefix: '/rpx/miraseeCommunity/getCommunityMeetings', section: 'community_meetings' },
  { prefix: '/rpx/miraseeCommunity/getCommunityPeople', section: 'community_people' },
  { prefix: '/rpx/miraseeCommunity/getCommunityLibrary', section: 'community_library' },
  {
    prefix: '/rpx/discussions/getDiscussion',
    section: 'discussion_thread',
    capture: ['discussionId'],
  },
  { prefix: '/rpx/comments/getComments', section: 'discussion_thread', capture: ['discussionId'] },
  // Order matters: getStudentMeetings (list) must precede getStudentMeeting
  // (single) because the latter is a prefix of the former.
  { prefix: '/rpx/meetings/getStudentMeetings', section: 'meetings_list' },
  { prefix: '/rpx/meetings/getStudentMeeting', section: 'meeting_page', capture: ['meetingId'] },
  { prefix: '/rpx/communityDashboard/getDashboardStats', section: 'admin_dashboard' },
  { prefix: '/rpx/communityDashboard/getWeeklyTrend', section: 'admin_dashboard' },
  { prefix: '/rpx/communityDashboard/getCurrentWeekStats', section: 'admin_dashboard' },
  { prefix: '/rpx/communityDashboard/getChampionPageActivity', section: 'admin_dashboard' },
  { prefix: '/rpx/communityDashboard/getChampionTrackingStart', section: 'admin_dashboard' },
];

// Guard captured side-channel ids before writing them into UUID columns.
// Endpoints today validate via Zod, but a future endpoint that reuses a
// captured field name (courseId/lessonId/etc.) without UUID validation would
// otherwise crash the upsert silently.
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export function classifyPath(path: string, body?: ClassificationBody): Classification | undefined {
  for (const noise of NOISE_PREFIXES) {
    if (path.startsWith(noise)) {
      return undefined;
    }
  }
  for (const mapping of RPC_MAPPINGS) {
    if (path.startsWith(mapping.prefix)) {
      const result: Classification = { section: mapping.section };
      if (mapping.capture && body) {
        for (const field of mapping.capture) {
          const value = body[field];
          if (value && UUID_RE.test(value)) {
            result[field] = value;
          }
        }
      }
      return result;
    }
  }
  // Authenticated request to an unmapped path — track as `other` so it shows
  // up in dashboard totals and we can extend the taxonomy when something
  // significant lands in this bucket.
  return { section: 'other' };
}

// Stable list for SQL aggregation queries that want to enumerate every
// possible section, including ones with zero visits in a given week.
export const ALL_SECTIONS: Section[] = [
  'course_home',
  'lesson_page',
  'community_feed',
  'community_meetings',
  'community_people',
  'community_library',
  'discussion_thread',
  'meeting_page',
  'meetings_list',
  'my_courses',
  'admin_dashboard',
  'other',
];

// "Real engagement" subset — sections that count as a Champion doing something
// deliberate beyond the default landing experience. Drives the at-risk and
// "engaged elsewhere" rollups.
//
// IMPORTANT: community_feed is NOT here. It's the default landing page on
// playground.mirasee.com — every Champion who logs in lands there
// automatically. Including it would credit every login as engagement and
// drive the at-risk count to ~zero. The "browsed feed, didn't engage" tile
// covers the feed-but-no-thread case separately.
export const ENGAGEMENT_SECTIONS: Section[] = [
  'lesson_page',
  'community_meetings',
  'community_people',
  'community_library',
  'discussion_thread',
  'meeting_page',
  'meetings_list',
];

// Human-readable labels for the dashboard UI and CSV headers.
export const SECTION_LABELS: Record<Section, string> = {
  course_home: 'Course Home',
  lesson_page: 'Lesson Page',
  community_feed: 'Community Feed',
  community_meetings: 'Community Meetings',
  community_people: 'Community People',
  community_library: 'Community Library',
  discussion_thread: 'Discussion Thread',
  meeting_page: 'Meeting Page',
  meetings_list: 'Meetings List',
  my_courses: 'My Courses',
  admin_dashboard: 'Admin Dashboard',
  other: 'Other',
};
